import { FC, useState, useRef } from 'react';
import classNames from 'classnames';
import styles from './barchart.module.sass';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';

// ──────────────────────────────────────────────────────────────
// BAR CHART — horizontal distribution bars
// Uses shared ChartTooltip (light glassmorphism).
// ──────────────────────────────────────────────────────────────

export type BarStatus = 'default' | 'pass' | 'warn' | 'breach';

const STATUS_COLORS: Record<BarStatus, string> = {
  default: 'var(--gt-accent, #4f46e5)',
  pass: '#059669',
  warn: '#F59E0B',
  breach: '#DC2626',
};

export interface BarChartItem {
  label: string;
  value: number;
  displayValue?: string;
  count?: string | number;
  status?: BarStatus;
}

export interface BarChartProps {
  items: BarChartItem[];
  tall?: boolean;
  maxValue?: number;
  className?: string;
}

export const BarChart: FC<BarChartProps> = ({
  items,
  tall = false,
  maxValue,
  className,
}) => {
  const max = maxValue ?? Math.max(...items.map((i) => i.value), 1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hIdx, setHIdx] = useState<number | null>(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  const onRowEnter = (idx: number, el: HTMLElement) => {
    setHIdx(idx);
    if (el) {
      const rowRect = el.getBoundingClientRect();
      const pct = items[idx].value / max;
      const trackEl = el.querySelector('[class*="barTrack"]') as HTMLElement;
      const trackRect = trackEl ? trackEl.getBoundingClientRect() : rowRect;
      setTipPos({
        x: trackRect.left + trackRect.width * pct + 8,
        y: rowRect.top + rowRect.height / 2,
      });
    }
  };

  const onRowLeave = () => setHIdx(null);

  return (
    <div ref={wrapRef} className={classNames(styles.barChart, className, 'relative')}>
      {items.map((item, idx) => (
        <div
          key={item.label + idx}
          className={classNames(styles.barRow, { [styles.barRowDim]: hIdx !== null && hIdx !== idx })}
          onMouseEnter={(e) => onRowEnter(idx, e.currentTarget)}
          onMouseLeave={onRowLeave}
        >
          <span className={styles.barLabel}>{item.label}</span>
          <div className={classNames(styles.barTrack, { [styles.tall]: tall })}>
            <div
              className={classNames(styles.barFill, styles[item.status || 'default'])}
              style={{ width: `${(item.value / max) * 100}%` }}
            />
          </div>
          {item.displayValue && (
            <span className={styles.barValue}>{item.displayValue}</span>
          )}
          {item.count != null && (
            <span className={styles.barCount}>{item.count}</span>
          )}
        </div>
      ))}

      <ChartTooltip
        visible={hIdx !== null}
        rows={hIdx !== null ? [{
          color: STATUS_COLORS[items[hIdx]?.status || 'default'],
          label: items[hIdx]?.label || '',
          value: items[hIdx]?.displayValue || String(items[hIdx]?.value || 0),
        }] : []}
        x={tipPos.x}
        y={tipPos.y}
        flip={tipPos.x > 300}
      />
    </div>
  );
};

BarChart.displayName = 'BarChart';

export default BarChart;
